import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, RotateCcw, FileText, Mic, FlaskConical, Loader2 } from "lucide-react";
import { toast } from "sonner";

const Bubble = ({ m }) => {
  if (m.role === "system") {
    return (
      <div className="flex justify-center my-1">
        <span className="text-[11px] text-zinc-400 bg-zinc-50 border border-zinc-100 px-3 py-1 rounded-full">{m.content}</span>
      </div>
    );
  }
  const isCandidate = m.role === "candidate";
  return (
    <div className={`flex ${isCandidate ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[80%] px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap rounded-2xl ${
        isCandidate ? "bg-[#E03C31] text-white rounded-tr-sm" : "bg-zinc-100 text-zinc-900 rounded-tl-sm"
      }`}>
        {!isCandidate && <div className="text-[10px] font-semibold uppercase opacity-60 mb-0.5">Ignite AI</div>}
        {m.content}
      </div>
    </div>
  );
};

export const TestConsole = ({ open, onOpenChange }) => {
  const [cid, setCid] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const scroll = () => setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }), 60);
  useEffect(() => { scroll(); }, [messages, busy]);

  const start = async () => {
    setBusy(true);
    try {
      const r = await api.post("/test/start");
      setCid(r.data.candidate.id);
      setMessages(r.data.messages);
    } catch { toast.error("Could not start test session"); }
    finally { setBusy(false); }
  };

  useEffect(() => { if (open && !cid) start(); /* eslint-disable-next-line */ }, [open]);

  const send = async () => {
    const t = input.trim();
    if (!t || !cid) return;
    setInput("");
    setMessages((p) => [...p, { id: "tmp" + Date.now(), role: "candidate", content: t }]);
    setBusy(true);
    try {
      const r = await api.post(`/test/${cid}/message`, { content: t });
      setMessages((p) => [...p.filter((m) => !String(m.id).startsWith("tmp")), ...r.data.messages]);
    } catch { toast.error("Message failed"); }
    finally { setBusy(false); }
  };

  const simulate = async (kind) => {
    if (!cid) return;
    setBusy(true);
    try {
      const r = await api.post(`/test/${cid}/simulate-upload?kind=${kind}`);
      setMessages((p) => [...p, ...r.data.messages]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const restart = async () => { setCid(null); setMessages([]); await start(); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 flex flex-col h-[80vh]" data-testid="test-console">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-zinc-100">
          <div className="flex items-center justify-between">
            <DialogTitle className="font-display text-lg flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-[#E03C31]" strokeWidth={1.5} /> Test Console
            </DialogTitle>
            <Button size="sm" variant="ghost" onClick={restart} className="gap-1.5 text-xs text-zinc-500" data-testid="test-restart-btn">
              <RotateCcw className="w-3.5 h-3.5" /> Restart
            </Button>
          </div>
          <p className="text-xs text-zinc-400">Chat with the bot exactly as a Messenger candidate would. Nothing here touches Facebook or your real inbox.</p>
        </DialogHeader>

        <div ref={scrollRef} className="flex-1 overflow-y-auto thin-scroll px-4 py-4 space-y-3 bg-zinc-50/40">
          {messages.map((m) => <Bubble key={m.id} m={m} />)}
          {busy && (
            <div className="flex justify-start"><div className="bg-zinc-100 rounded-2xl rounded-tl-sm px-4 py-3 flex gap-1">
              <span className="w-2 h-2 rounded-full bg-zinc-400 typing-dot" />
              <span className="w-2 h-2 rounded-full bg-zinc-400 typing-dot" style={{ animationDelay: "0.2s" }} />
              <span className="w-2 h-2 rounded-full bg-zinc-400 typing-dot" style={{ animationDelay: "0.4s" }} />
            </div></div>
          )}
        </div>

        <div className="px-3 py-2 border-t border-zinc-100 flex gap-2">
          <Button size="sm" variant="outline" className="flex-1 gap-1.5 text-xs" onClick={() => simulate("resume")} disabled={busy} data-testid="test-simulate-resume">
            <FileText className="w-3.5 h-3.5" /> Simulate resume
          </Button>
          <Button size="sm" variant="outline" className="flex-1 gap-1.5 text-xs" onClick={() => simulate("voice")} disabled={busy} data-testid="test-simulate-voice">
            <Mic className="w-3.5 h-3.5" /> Simulate voice
          </Button>
        </div>

        <div className="px-3 pb-3 flex items-center gap-2">
          <Input
            data-testid="test-message-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Type a candidate message..."
            className="flex-1"
          />
          <Button size="icon" className="bg-[#E03C31] hover:bg-[#C9362C] shrink-0" onClick={send} disabled={busy} data-testid="test-send-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={1.5} />}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
